import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPaginatedRouteList, searchRoutes } from '../../features/tripcenter/routeApi';
import {
    selectRouteList,
    setSelectedRoute,
    routePageIndexSelector,
    routesPerPageSelector,
    selectSearchQuery,
    setSearchQuery,
    selectPagination,
    routeRevisedOnlySelector,
    setRouteRevisedOnly,
} from '../../features/tripcenter/routeSlice';
import {
    List, TextField, InputAdornment, ListItem, Paper, Box, Typography, Divider,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import RouteListItem from './routeViewComponents/RouteListItem';
import Icon from '@mdi/react';
import { mdiFilter } from '@mdi/js';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import RouteIcon from '@mui/icons-material/RouteOutlined';
import RouteDetailView from './routeViewComponents/RouteDetailView';

import {
    selectSelectedRoute,
    selectIsDetailViewOpen,
    setDetailViewOpen,
} from '../../features/tripcenter/routeSlice';
const RoutesSideBarBody = () => {
    const dispatch = useDispatch();
    const routeList = useSelector(selectRouteList);
    const searchQuery = useSelector(selectSearchQuery);
    const selectedRoute = useSelector(selectSelectedRoute);
    const isDetailViewOpen = useSelector(selectIsDetailViewOpen);
    const routePageIndex = useSelector(routePageIndexSelector);
    const routesPerPage = useSelector(routesPerPageSelector);
    const pagination = useSelector(selectPagination);
    const revisedOnly = useSelector(routeRevisedOnlySelector);
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [draftRevisedOnly, setDraftRevisedOnly] = useState(revisedOnly);

    const handleFilterDialogOpen = () => {
        setDraftRevisedOnly(revisedOnly);
        setFilterDialogOpen(true);
    };

    const handleFilterApply = () => {
        dispatch(setRouteRevisedOnly(draftRevisedOnly));
        setFilterDialogOpen(false);
    };

    useEffect(() => {
        if (searchQuery || revisedOnly) {
            const offset = (routePageIndex - 1) * routesPerPage;
            dispatch(searchRoutes({
                searchQuery: searchQuery || '',
                revisedOnly,
                limit: routesPerPage,
                offset
            }));
        } else {
            const offset = (routePageIndex - 1) * routesPerPage;
            dispatch(getPaginatedRouteList({ limit: routesPerPage, offset }));
        }
        return () => {
            dispatch(setSelectedRoute(null));
        }
    }, [dispatch, routePageIndex, routesPerPage, searchQuery, revisedOnly]);
    //console.log("routes", routeList);
    return (
        <div style={{ marginTop: '5px', position: 'relative', marginBottom: '30px' }}>
            <List dense>
                <ListItem
                    sx={{
                        borderRadius: 0.5,
                        mb: 0.15,
                        boxShadow: 1,
                        bgcolor: '#fafafa',
                        padding: 1,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Route Name, Number, Location"
                        value={searchQuery}
                        onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Tooltip title={revisedOnly ? 'Route filters: revised routes only' : 'Route filters'}>
                                        <IconButton
                                            size="small"
                                            aria-label="Open route filters"
                                            onClick={handleFilterDialogOpen}
                                            sx={{ color: revisedOnly ? 'primary.main' : 'text.secondary' }}
                                        >
                                            <Icon path={mdiFilter} size={0.8} />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        minWidth: 'fit-content',
                        backgroundColor: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                    }}>
                        <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }}>
                            {routeList.length > pagination?.limit ? pagination?.limit : routeList.length}
                        </span>
                        <span style={{ color: 'rgba(0, 0, 0, 0.38)', fontSize: '0.875rem' }}>
                            / {pagination?.total}
                        </span>
                    </div>
                </ListItem>
                {routeList && routeList.length > 0 ? (
                    routeList.map((route) => (
                        <RouteListItem
                            key={route._id}
                            route={route}
                        />
                    ))
                ) : (
                    <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                            m: 1,
                            p: 4,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            border: '1px dashed',
                            borderColor: 'divider'
                        }}
                    >
                            {routeList && routeList?.length > 0 ? (
                            /* Show this when routes exist but filter returned no results */
                            <>
                                <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary' }} />

                                <Box>
                                    <Typography variant="h6" color="text.primary" fontWeight={500} gutterBottom>
                                        No Routes Found
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        Try adjusting your search criteria
                                    </Typography>
                                </Box>

                                <Divider sx={{ width: '40%', my: 1 }} />

                                <Typography variant="caption" color="text.disabled">
                                    Modify your filter text to see available routes
                                </Typography>
                            </>
                        ) : (
                            /* Show this when no routes exist at all */
                            <>
                                <RouteIcon sx={{ fontSize: 48, color: 'text.secondary' }} />

                                <Box>
                                    <Typography variant="h6" color="text.primary" fontWeight={500} gutterBottom>
                                        No Routes Available
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        You haven't created any routes yet
                                    </Typography>
                                </Box>

                                <Divider sx={{ width: '40%', my: 1 }} />

                                <Typography variant="caption" color="text.disabled">
                                    Use the "New Route" button above to create your first route
                                </Typography>
                            </>
                        )}
                    </Paper>
                )}
            </List>
            <RouteDetailView
                open={isDetailViewOpen}
                onClose={() => dispatch(setDetailViewOpen(false))}
                route={selectedRoute}
            />
            <Dialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Route Filters</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Choose which routes to include in the search results.
                    </Typography>
                    <RadioGroup
                        value={draftRevisedOnly ? 'revised' : 'all'}
                        onChange={(event) => setDraftRevisedOnly(event.target.value === 'revised')}
                    >
                        <FormControlLabel value="all" control={<Radio />} label="All routes" />
                        <FormControlLabel
                            value="revised"
                            control={<Radio />}
                            label="Revised routes only"
                        />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleFilterApply}>Apply</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RoutesSideBarBody;
